import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { FileText, Plus, Trash2, Edit } from 'lucide-react';
import { PITCH_TEMPLATE } from '../utils/constants';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PitchesPage() {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};
  const [pitches, setPitches] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [editingPitch, setEditingPitch] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    outlet: '',
    content: PITCH_TEMPLATE
  });

  useEffect(() => {
    fetchPitches();
  }, []);

  const fetchPitches = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pitches`, { headers: authHeader });
      setPitches(response.data);
    } catch (error) {
      console.error('Failed to fetch pitches:', error);
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingPitch(null);
    setFormData({ title: '', outlet: '', content: PITCH_TEMPLATE });
  };

  const handleEdit = (pitch) => {
    setIsCreating(false);
    setEditingPitch(pitch);
    setFormData({
      title: pitch.title,
      outlet: pitch.outlet || '',
      content: pitch.content
    });
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    try {
      if (editingPitch) {
        await axios.put(`${BACKEND_URL}/api/pitches/${encodeURIComponent(editingPitch.title)}`, formData, { headers: authHeader });
        toast.success('Pitch updated!');
      } else {
        await axios.post(`${BACKEND_URL}/api/pitches`, formData, { headers: authHeader });
        toast.success('Pitch saved!');
      }
      setIsCreating(false);
      setEditingPitch(null);
      fetchPitches();
    } catch (error) {
      toast.error('Failed to save pitch');
    }
  };

  const handleDelete = async (title) => {
    if (!window.confirm('Are you sure you want to delete this pitch?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/pitches/${encodeURIComponent(title)}`, { headers: authHeader });
      toast.success('Pitch deleted');
      fetchPitches();
    } catch (error) {
      toast.error('Failed to delete pitch');
    }
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingPitch(null);
  };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-12 flex items-center justify-between">
        <div>
          <h1 className="text-5xl font-bold tracking-tight mb-2">Pitch Templates</h1>
          <p className="text-lg text-muted-foreground font-['IBM_Plex_Sans']">
            Build and save your Atlantic-style pitches
          </p>
        </div>
        {!isCreating && !editingPitch && (
          <Button
            data-testid="btn-new-pitch"
            onClick={handleCreate}
            className="uppercase tracking-wide font-medium"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Pitch
          </Button>
        )}
      </div>

      {/* Form */}
      {(isCreating || editingPitch) && (
        <div className="mb-8 bg-card border border-border p-8 rounded-sm">
          <h2 className="text-2xl font-bold tracking-tight mb-6">
            {editingPitch ? 'Edit Pitch' : 'New Pitch'}
          </h2>
          
          <div className="space-y-6">
            <div>
              <Label htmlFor="title" className="uppercase tracking-widest text-xs font-medium mb-2 block">
                Pitch Title *
              </Label>
              <Input
                id="title"
                data-testid="input-pitch-title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Climate Migration Story"
                className="font-['IBM_Plex_Sans']"
              />
            </div>

            <div>
              <Label htmlFor="outlet" className="uppercase tracking-widest text-xs font-medium mb-2 block">
                Target Outlet
              </Label>
              <Input
                id="outlet"
                data-testid="input-pitch-outlet"
                value={formData.outlet}
                onChange={(e) => setFormData({ ...formData, outlet: e.target.value })}
                placeholder="e.g., The Atlantic, The New Yorker"
                className="font-['IBM_Plex_Sans']"
              />
            </div>

            <div>
              <Label htmlFor="content" className="uppercase tracking-widest text-xs font-medium mb-2 block">
                Pitch Content
              </Label>
              <Textarea
                id="content"
                data-testid="textarea-pitch-content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={16}
                className="font-['Source_Serif_4'] text-base leading-relaxed"
              />
              <p className="text-xs text-muted-foreground mt-2 font-['IBM_Plex_Sans']">
                Follow the 5-7 sentence Atlantic pitch format
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                data-testid="btn-save-pitch"
                onClick={handleSave}
                className="uppercase tracking-wide font-medium"
              >
                {editingPitch ? 'Update Pitch' : 'Save Pitch'}
              </Button>
              <Button
                data-testid="btn-cancel-pitch"
                onClick={handleCancel}
                variant="outline"
                className="uppercase tracking-wide font-medium"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Pitch List */}
      {pitches.length === 0 ? (
        <div className="text-center py-16 bg-card border border-border rounded-sm">
          <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground font-['IBM_Plex_Sans'] mb-4">
            No pitches yet. Create your first pitch template!
          </p>
          {!isCreating && (
            <Button
              data-testid="btn-create-first-pitch"
              onClick={handleCreate}
              className="uppercase tracking-wide font-medium"
            >
              Create First Pitch
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {pitches.map((pitch, index) => (
            <div
              key={index}
              data-testid={`pitch-${index}`}
              className="bg-card border border-border p-6 rounded-sm hover:border-primary/50 transition-colors"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold tracking-tight mb-1">{pitch.title}</h3>
                  {pitch.outlet && (
                    <p className="text-sm text-muted-foreground font-['IBM_Plex_Sans']">
                      Target: {pitch.outlet}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid={`btn-edit-pitch-${index}`}
                    onClick={() => handleEdit(pitch)}
                    variant="ghost"
                    size="sm"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    data-testid={`btn-delete-pitch-${index}`}
                    onClick={() => handleDelete(pitch.title)}
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="prose max-w-none">
                <pre className="text-foreground font-['Source_Serif_4'] leading-relaxed whitespace-pre-wrap text-sm bg-muted/30 p-4 rounded-sm">
                  {pitch.content}
                </pre>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

